import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getConsent, setConsent } from "@/lib/tracking";

// Soft, brand-true consent banner.
//
// Two regimes, decided by the visitor's country from /api/geo (Cloudflare's
// CF-IPCountry; no IP is kept):
//   * United States — no consent-before-tracking obligation applies. Consent is
//     granted by default and a one-line notice offers an opt-out. Without this
//     the Meta pixel received 13 page views in 28 days across the whole site,
//     and a conversions campaign cannot learn from events it never sees.
//   * Everywhere else (EU/UK/rest of world) — unchanged: denied until the
//     visitor explicitly accepts; Pixel/GA4 stay in "consent: denied" mode and
//     the Worker keeps only first-party analytics.
// Founder ruling 2026-09-14 ("for privacy do what you think makes sense").
// The Dharma app's opt-in-only stance is a separate policy and is untouched.
// US and Canada: no consent-before-tracking rule applies (PIPEDA accepts implied consent for analytics/ads).
// UK and EU (incl. France): opt-in stays — UK GDPR / CNIL require it.
const IMPLIED_CONSENT_COUNTRIES = new Set(["US", "CA"]);

export default function ConsentBanner() {
  const [show, setShow] = useState(false);
  const [implied, setImplied] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (c.decided) return;
    let cancelled = false;
    fetch("/api/geo", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((geo) => {
        if (cancelled) return;
        if (geo && IMPLIED_CONSENT_COUNTRIES.has(geo.country)) {
          setConsent(true, true);
          setImplied(true);
        }
        setShow(true);
      })
      .catch(() => { if (!cancelled) setShow(true); });
    return () => { cancelled = true; };
  }, []);

  const handle = (analytics, marketing) => {
    setConsent(analytics, marketing);
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50"
        >
          {implied ? (
            <div className="card-shrine rounded-sm p-4 md:p-5 bg-cream/95 backdrop-blur-md">
              <p className="font-serif text-sm md:text-base text-ink leading-snug">
                We use minimal analytics and share ad clicks with the platforms
                our ads come from. You can opt out any time.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => handle(true, true)} className="btn-pill btn-pill-solid">
                  OK
                </button>
                <button onClick={() => handle(false, false)} className="btn-pill text-ink-mute">
                  Opt out
                </button>
              </div>
            </div>
          ) : (
          <div className="card-shrine rounded-sm p-5 md:p-6 bg-cream/95 backdrop-blur-md">
            <p className="font-serif text-base md:text-lg text-ink leading-snug">
              We use minimal first-party analytics to understand which stories
              find their readers. With your permission, we also share clicks
              with the platforms our ads come from.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <button
                onClick={() => handle(true, true)}
                className="btn-pill btn-pill-solid"
              >
                Accept all
              </button>
              <button
                onClick={() => handle(true, false)}
                className="btn-pill"
              >
                Analytics only
              </button>
              <button
                onClick={() => handle(false, false)}
                className="btn-pill text-ink-mute"
              >
                Decline
              </button>
            </div>
          </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
